// src/components/settings/FailureView.tsx
//
// Settings → Failures: the org admin's single failure surface (Story 4.8). One
// table over five categories drawn from four feeds (dead letters incl. bounces,
// outbox delivery failures, unrecognized-caller attempts, and Story 6.2's
// caller-verification lockouts), unified newest-first by
// mergeFailureRows and served by useFailures → failuresService. The view is
// READ-ONLY (Decision 9): no acknowledge/resolve/dismiss/re-send — remediation
// lives on the pointed-to objects (fix the rep's email, respond again from the
// ticket, fix routing/roster). Live updates arrive via useFailures' realtime
// refetch; the flag state ("still flagged" / "updated since") is read live.
//
// Chip tones (DESIGN.md): bounce + verification lockout = orange (advisory),
// dead-letter kinds + outbox = red (critical), unrecognized-caller = neutral gray
// — every chip carries a text label. Voice: calm counts-and-verbs; a failure is a
// fact, not an alarm.

import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MailWarning } from 'lucide-react'
import { useFailures } from '../../hooks/useFailures'
import { useAuthStore } from '../../stores/authStore'
import { formatRelativeTime, formatDateTime } from '../../utils/dateUtils'
import {
  type FailureCategory,
  type FailureChipTone,
  type FailureRow,
  FAILURES_PAGE_SIZE,
  failureChipTone,
  failureLabel,
} from '../../utils/failureDisplay'

const CHIP_CLASSES: Record<FailureChipTone, string> = {
  orange: 'bg-orange-100 text-orange-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-800',
}

// The category filter options (a "kind" filter over the unified categories).
const CATEGORY_OPTIONS: { value: FailureCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'All failures' },
  { value: 'dead_letter', label: 'Dead letters' },
  { value: 'bounce', label: 'Bounces' },
  { value: 'outbox', label: 'Delivery failures' },
  { value: 'unrecognized_caller', label: 'Unregistered callers' },
  // Story 6.2 (FR-42): 2FA lockouts.
  { value: 'verification', label: 'Verification lockouts' },
]

function Chip({ row }: { row: FailureRow }) {
  const tone = failureChipTone(row.category)
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CHIP_CLASSES[tone]}`}
    >
      {failureLabel(row)}
    </span>
  )
}

export function FailureView() {
  const { rows, loading, error, refetch } = useFailures()
  const { user } = useAuthStore()
  const isSuperAdmin = user?.role === 'super_admin'

  const [category, setCategory] = useState<FailureCategory | 'all'>('all')
  const [includeAutoReplies, setIncludeAutoReplies] = useState(false)
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Apply the filters over the merged rows (auto-replies excluded by default —
  // 4.6's noise-tier flag; the toggle includes them).
  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (!includeAutoReplies && r.kind === 'auto_reply') return false
      if (category !== 'all' && r.category !== category) return false
      return true
    })
  }, [rows, includeAutoReplies, category])

  // Clamp the page whenever the filtered set shrinks under it.
  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / FAILURES_PAGE_SIZE)
  )
  const safePage = Math.min(page, totalPages)
  const from = (safePage - 1) * FAILURES_PAGE_SIZE
  const pageRows = filtered.slice(from, from + FAILURES_PAGE_SIZE)
  const showingFrom = filtered.length === 0 ? 0 : from + 1
  const showingTo = Math.min(from + FAILURES_PAGE_SIZE, filtered.length)

  const resetPage = () => setPage(1)

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">Failures</h2>
        <p className="text-sm text-gray-600 mt-1">
          Undeliverable or unattributable messages, email and SMS delivery
          failures, and calls from unregistered numbers. Everything here is a
          record — fix the pointed-to record (a rep's email or number, a ticket)
          to resolve it.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label
            htmlFor="failure-category"
            className="text-sm font-medium text-gray-700"
          >
            Kind
          </label>
          <select
            id="failure-category"
            className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm"
            value={category}
            onChange={e => {
              setCategory(e.target.value as FailureCategory | 'all')
              resetPage()
            }}
          >
            {CATEGORY_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            checked={includeAutoReplies}
            onChange={e => {
              setIncludeAutoReplies(e.target.checked)
              resetPage()
            }}
          />
          Include auto-replies
        </label>
      </div>

      {loading ? (
        <div className="space-y-3" aria-hidden="true">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-center justify-between"
        >
          <span>{error}</span>
          <button
            className="ml-4 underline hover:no-underline"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <MailWarning className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No delivery problems.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Kind</th>
                  <th className="py-2 pr-4 font-medium">Detail</th>
                  <th className="py-2 pr-4 font-medium">Attribution</th>
                  {isSuperAdmin && (
                    <th className="py-2 pr-4 font-medium">Org</th>
                  )}
                  <th className="py-2 pr-4 font-medium">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map(row => {
                  const expanded = expandedId === row.id
                  const flagState =
                    row.category === 'bounce'
                      ? row.repEmailBouncedAt
                        ? 'Address still flagged'
                        : 'Address updated since'
                      : null
                  // Story 6.1 review: an sms delivery failure is rep-only by
                  // construction, and its dominant terminal causes (invalid
                  // number, opted out, unreachable handset) are all fixed on the
                  // rep record. Without this the column rendered '—' for the one
                  // category whose remediation IS the roster.
                  const smsRepRemediation =
                    row.category === 'outbox' &&
                    row.channel === 'sms' &&
                    !!row.repId
                  return (
                    <Fragment key={row.id}>
                      <tr
                        className="group cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedId(expanded ? null : row.id)}
                      >
                        <td className="py-2 pr-4 align-top">
                          <Chip row={row} />
                        </td>
                        <td className="py-2 pr-4 text-gray-700 align-top">
                          <button
                            className="text-left hover:text-gray-900 focus:outline-none focus:underline"
                            aria-expanded={expanded}
                            onClick={e => {
                              e.stopPropagation()
                              setExpandedId(expanded ? null : row.id)
                            }}
                          >
                            {row.summary}
                          </button>
                        </td>
                        <td className="py-2 pr-4 text-gray-700 align-top">
                          <div className="flex flex-col gap-0.5">
                            {row.category === 'bounce' && (
                              <>
                                <Link
                                  to="/settings/reps"
                                  className="text-brand-600 hover:underline"
                                  onClick={e => e.stopPropagation()}
                                >
                                  View rep roster
                                </Link>
                                {flagState && (
                                  <span className="text-xs text-gray-500">
                                    {flagState}
                                  </span>
                                )}
                              </>
                            )}
                            {row.ticketId && (
                              <Link
                                to={`/tickets/${row.ticketId}`}
                                className="text-brand-600 hover:underline"
                                onClick={e => e.stopPropagation()}
                              >
                                View ticket
                              </Link>
                            )}
                            {row.category === 'unrecognized_caller' && (
                              <span className="text-xs text-gray-500">
                                An adoption signal — register the number on the
                                rep roster.
                              </span>
                            )}
                            {smsRepRemediation && (
                              <>
                                <Link
                                  to="/settings/reps"
                                  className="text-brand-600 hover:underline"
                                  onClick={e => e.stopPropagation()}
                                >
                                  View rep roster
                                </Link>
                                <span className="text-xs text-gray-500">
                                  Check the number on file for this rep.
                                </span>
                              </>
                            )}
                            {/* Story 6.2: the remediation for a lockout lives on
                                the rep record — usually a stale phone number. */}
                            {row.category === 'verification' && (
                              <>
                                <Link
                                  to="/settings/reps"
                                  className="text-brand-600 hover:underline"
                                  onClick={e => e.stopPropagation()}
                                >
                                  View rep roster
                                </Link>
                                <span className="text-xs text-gray-500">
                                  Check the number on file for this rep.
                                </span>
                              </>
                            )}
                            {!row.ticketId &&
                              !smsRepRemediation &&
                              row.category !== 'bounce' &&
                              row.category !== 'verification' &&
                              row.category !== 'unrecognized_caller' && (
                                <span className="text-gray-400">—</span>
                              )}
                          </div>
                        </td>
                        {isSuperAdmin && (
                          <td className="py-2 pr-4 text-gray-500 align-top text-xs">
                            {row.organizationId ?? '(unattributed)'}
                          </td>
                        )}
                        <td
                          className="py-2 pr-4 text-gray-500 align-top whitespace-nowrap"
                          title={
                            row.occurredAt
                              ? formatDateTime(row.occurredAt)
                              : undefined
                          }
                        >
                          {row.occurredAt
                            ? formatRelativeTime(row.occurredAt)
                            : '—'}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-gray-50">
                          <td
                            colSpan={isSuperAdmin ? 5 : 4}
                            className="py-3 px-4"
                          >
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words">
                              {JSON.stringify(row.detail, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span aria-live="polite">
              Showing {showingFrom}–{showingTo} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1}
              >
                Prev
              </button>
              <span>
                Page {safePage} of {totalPages}
              </span>
              <button
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
